/**
 * Basic sanity checks
 */
export const isNonEmptyNumber = (n: unknown): n is number =>
  n !== undefined && n !== null && typeof n === 'number';
export const isValidNumber = (n: number): boolean => isNonEmptyNumber(n) && !Number.isNaN(n);
export const isValidNonZeroNumber = (n: number): boolean => isValidNumber(n) && n !== 0;

/**
 * Threshold and range checking
 */
export const isNumberAboveThreshold = (
  value: number,
  threshold: number,
  included = false
): boolean =>
  isValidNumber(threshold) && (value > threshold || Boolean(included && value === threshold));

export const isNumberBelowThreshold = (
  value: number,
  threshold: number,
  included = false
): boolean =>
  isValidNumber(threshold) && (value < threshold || Boolean(included && value === threshold));

export interface Range {
  min?: number;
  max?: number;
  minIncluded?: boolean;
  maxIncluded?: boolean;
}

export const isNumberWithinRange = (value: number, range: Range = {}): boolean => {
  if (isValidNumber(value)) {
    if (!isValidNumber(range.min)) {
      // Only max availabe, check below max
      return isNumberBelowThreshold(value, range.max, range.maxIncluded);
    } else if (!isValidNumber(range.max)) {
      // Only min availabe, check above max
      return isNumberAboveThreshold(value, range.min, range.minIncluded);
    } else {
      // Both limits abailable, check within.
      return (
        isNumberBelowThreshold(value, range.max, range.maxIncluded) &&
        isNumberAboveThreshold(value, range.min, range.minIncluded)
      );
    }
  } else {
    return false;
  }
};

/**
 * Number utils
 */
const TEN_FACTOR = 10;

export const roundFloat = (n: number, decimals: number) => {
  if (isValidNumber(n) && !Number.isInteger(n)) {
    const decimalFactor = TEN_FACTOR ** decimals;
    return Math.round(n * decimalFactor) / decimalFactor;
  } else {
    return n;
  }
};

/**
 * Converts a given bounded value from its source range to a desired target range. For example:
 * Value 0.5 from a source range [0, 1] to a target range [0, 100] would be converted to 50.
 * @param value Target value to be converted.
 * @param targetRange Target range in tuple format [number, number].
 * @param sourceRange Source range in tuple format [number, number]. Default [0,1]
 * @param clamp If clamping is enabled, values outside the target range are truncated to stay
 * whithin target range limits. If clampin is disabled, values can be extrapolated outside the
 * target range.
 * @returns Converted value.
 */
export const convertValueRange = (
  value: number,
  targetRange: [number, number],
  sourceRange: [number, number] = [0, 1],
  clamp = false
) => {
  if (!sourceRange || !targetRange) {
    return value;
  }
  const srMin = Math.min(...sourceRange);
  const srMax = Math.max(...sourceRange);
  const trMin = Math.min(...targetRange);
  const trMax = Math.max(...targetRange);
  const valuePerc = (value - srMin) / (srMax - srMin);
  const convertedValue = valuePerc * (trMax - trMin) + trMin;
  return clamp ? Math.min(Math.max(convertedValue, trMin), trMax) : convertedValue;
};

const SIN_FACTOR = 10000;

/**
 * Factory to generate pseudo random numbers based on a seed. Given its simple implementation
 * this is not a uniform sampler, it is biased towards 0 and 1 and therefore should not be
 * used in critical situations.
 * @param seed Numeric seed.
 * @retuns Function to generate random seeded numbers in the range [0, 1]
 */
export const pseudoRandomSeeded = (seed: number) => () => {
  const x = Math.sin(seed) * SIN_FACTOR;
  return x - Math.floor(x);
};
